import { BreakpointObserver } from "@angular/cdk/layout";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from "@angular/core";
import * as composeActions from "@bca-compose/store/compose.actions";
import { MediaQuery } from "@bca-core/models/_enums";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { UIService } from "@bca-core/services/ui.service";
import { ReceiptDialog } from "@bca-receipt/receipt/receipt-dialog";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  DialogIdentifier,
  ProductIdentifier,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { DialogId, ProductId } from "@bca-shared/models/_types";
import { IProduct } from "@bca-shared/models/product";
import { cloneProducts } from "@bca-shared/shared.constants";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  configuredProduct,
  selectAllProducts,
  selectTotalPrice,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";

import {
  combineLatest,
  filter,
  map,
  take,
  takeUntil,
  tap,
} from "rxjs/operators";

import { AvoDataTransformService } from "@bca-shared/utils/avo/avo-data-transform.service";
import { AvoEventTypes } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import { Observable, Subject } from "rxjs";
@Component({
  selector: "bca-products-overview",
  templateUrl: "products-overview.component.html",
  styleUrls: ["products-overview.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsOverviewComponent {
  @ViewChild("showNewLayoutTrue") showNewLayoutTrue: ElementRef;
  @ViewChild("showNewLayoutFalse") showNewLayoutFalse: ElementRef;
  @Input()
  mobileQueryMatches$ = this._breakPoint
    .observe(MediaQuery.MAX_WIDTH_640)
    .pipe(map(({ matches }) => matches));

  showMobileReceiptFooter$ = this._uiService.showMobileReceiptFooter$;
  products$ = this._store$.pipe(
    select(({ compose }) => {
      const { products } = compose;
      return cloneProducts(products);
    }),
  );

  configuredProduct$ = this._store$.pipe(
    select(({ compose }) => compose.configuredProduct),
  );
  hideSticker$ = this.configuredProduct$.pipe(
    combineLatest(this.mobileQueryMatches$),
    map(([configuredProduct, mobileQueryMatches]) => {
      return configuredProduct != null && !mobileQueryMatches;
    }),
  );
  isReceiptDialogOpen$ = this._store$.pipe(
    select(({ app }) => app.dialogStatus),
    map((dialogStatus) => dialogStatus[DialogIdentifier.Receipt]),
  );
  productsLoading$ = this._store$.pipe(
    select(({ compose }) => compose.productsLoading),
  );
  selectedProduct$ = this._store$.pipe(
    select(({ compose }) => compose.selectedProduct),
  );
  totalProductCount$ = this._store$.pipe(select(selectTotalProductCount));
  totalReceiptPrice$ = this._store$.pipe(select(selectTotalPrice));
  routeLoading$ = this._store$.pipe(
    select(({ compose }) => compose.routeLoading),
  );
  stickyReceipt$ = this.totalProductCount$.pipe(map((count) => count < 4));
  showPhoneNumber$ = this._store$.pipe(select(({ app }) => app.showPhone));

  ProductIdentifier = ProductIdentifier;
  maxBuyableProducts = environment.receipt.maxBuyableProducts;

  limitedProducts$: Observable<IProduct[]> = this.products$.pipe(
    map((x) =>
      x.filter(
        (product) =>
          product.identifier === "BCB" || product.identifier === "BCD",
      ),
    ),
  );
  limitlessProducts$: Observable<IProduct[]> = this.products$.pipe(
    map((x) => {
      return x.filter(
        (product) =>
          product.identifier === "BCT" ||
          product.identifier === "BCS" ||
          product.identifier === "VRIJ",
      );
    }),
  );

  allProducts$: Observable<IProduct[]> = this.products$.pipe(map((x) => x));
  showNewProductLayout = true;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private _store$: Store<IRootState>,
    private _uiService: UIService,
    private _dialogService: DialogService,
    private _breakPoint: BreakpointObserver,
    private _page: PageScrollService,
    private readonly avoService: AvoService,
    private avoTransformService: AvoDataTransformService,
  ) {}

  ngOnInit(): void {
    this._store$
      .pipe(
        select(({ userData }) => userData.selectedProduct),
        filter((selectedProduct) => !!selectedProduct),
        takeUntil(this._destroy$),
      )
      .subscribe((selectedProduct) => {
        this.handleSelectedProduct(selectedProduct);
      });

    this._store$.dispatch(
      this._createAction(new composeActions.FetchProducts()),
    );

    this.products$
      .pipe(
        filter((products) => Array.isArray(products) && products.length > 0),
        take(1),
        tap((products) => {
          this.avoService.viewItemList(
            this.avoTransformService.transformViewItemListAction(products),
          );
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onProductSelected(product: IProduct): void {
    const { identifier } = product;
    if (identifier === ProductIdentifier.Traject) {
      this.configuredProduct$
        .pipe(take(1), takeUntil(this._destroy$))
        .subscribe((configuredProduct) => {
          if (configuredProduct && configuredProduct.route !== null) {
            this.onRouteSelected(configuredProduct);
          } else {
            this.onRouteDialogOpened();
          }
        });
      return;
    }
    this.dispatchProductActions(product);
  }

  onReadMoreOpened(product: IProduct): void {
    this._store$.dispatch(
      this._createAction(
        new appActions.SetDialogStatus({
          visible: true,
          identifier: product.identifier,
        }),
      ),
    );
  }

  onRouteDialogOpened(): void {
    setTimeout(() => {
      this._store$.dispatch(
        this._createAction(
          new appActions.SetDialogStatus({
            visible: true,
            identifier: DialogIdentifier.ChooseRoute,
          }),
        ),
      );
    });
  }

  onCloseRouteDialog() {
    this._store$
      .select(configuredProduct)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe((configuredProduct) => {
        if (configuredProduct && configuredProduct.route !== null) {
          this.onRouteSelected(configuredProduct);
        }
      });
  }

  onRouteSelected(product: IProduct): void {
    this.dispatchProductActions(product);
  }

  onReceiptOpen(): void {
    const receiptDialog = this._dialogService.open(
      ReceiptDialog,
      DialogIdentifier.Receipt,
      null,
      null,
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    receiptDialog.instance
      .nextPage$()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.nextRoute());
    receiptDialog.instance
      .addProduct$()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.addNewProduct());
  }

  addNewProduct(): void {
    this.selectedProduct$
      .pipe(
        takeUntil(this._destroy$),
        take(1),
        filter((selectedProduct) => selectedProduct != null),
      )
      .subscribe((selectedProduct) => {
        this.handleAvoEvents("link_click", null);

        this._store$.dispatch(
          this._createAction(new composeActions.SetConfiguredProduct(null)),
        );
        this._store$.dispatch(
          this._createAction(new receiptActions.AddProduct(selectedProduct)),
        );
        this._store$.dispatch(
          this._createAction(new composeActions.SelectProduct(null)),
        );
        this._page.scrollTo(ScrollPositionId.TOP, {
          timeout: 275,
          scrollOffset: 250,
        });
      });
  }

  async nextRoute(): Promise<void> {
    this.selectedProduct$
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe((selectedProduct) => {
        this.handleAvoEvents("add_to_cart", null);

        this._store$.dispatch(
          this._createAction(
            new userDataActions.SelectProduct(selectedProduct),
          ),
        );

        if (selectedProduct) {
          this._store$.dispatch(
            this._createAction(new composeActions.SetConfiguredProduct(null)),
          );
          this._store$.dispatch(
            this._createAction(new receiptActions.AddProduct(selectedProduct)),
          );
          this._store$.dispatch(
            this._createAction(new composeActions.SelectProduct(null)),
          );
        }
      });
    this._store$.dispatch(this._createAction(new appActions.NextRoute()));
  }

  trackByProductIdentifier(i: number, product: IProduct): ProductId {
    return product.identifier;
  }
  closeProductDialog(): void {
    this.handleAvoEvents("close_overlay");
  }

  private dispatchProductActions(product: IProduct): void {
    this._store$.dispatch(
      this._createAction(
        new composeActions.SelectProduct({ product, scrollToExtras: true }),
      ),
    );
    this.handleAvoEvents("select_item", product.identifier);
  }

  private handleSelectedProduct(selectedProduct: IProduct): void {
    const { identifier } = selectedProduct;
    if (identifier === ProductIdentifier.Traject) {
      this._store$.dispatch(
        this._createAction(
          new composeActions.SetConfiguredProduct(selectedProduct),
        ),
      );
    }

    this._store$.dispatch(
      this._createAction(
        new composeActions.SelectProduct({
          product: selectedProduct,
          scrollToExtras: false,
        }),
      ),
    );
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, ProductsOverviewComponent);
  }

  private handleAvoEvents(
    eventType: AvoEventTypes,
    productId?: ProductId,
  ): void {
    switch (eventType) {
      case "link_click":
        this.avoService.linkClick({
          newTab: false,
          externalLink: false,
          uniqueName: "add_card_on_compose_page",
          description: "button voor het toevoegen kaart aan bestelling",
          clickDestination: "ns-zakelijk|checkout|samenstellen",
          clickText: "Nog een kaart bestellen",
          websiteSection: "ns-zakelijk|checkout|samenstellen",
        });
        break;
      case "add_to_cart":
        let addToCartData;

        this._store$
          .pipe(select(selectAllProducts), takeUntil(this._destroy$))
          .subscribe((products: IProduct[]) => {
            addToCartData =
              this.avoTransformService.transformCartAction(products);
          });

        this.avoService.addToCart(addToCartData);
        break;
      // case "close_overlay":

      //   break;
      case "select_item":
        this.products$
          .pipe(
            take(1),
            tap((products) => {
              products.forEach((product, index) => {
                if (product.identifier === productId) {
                  this.avoService.selectItem({
                    itemListId: "BCA",
                    itemListName: "NS-Business card",
                    listPosition1: index,
                    items: [this.avoTransformService.transformItem(product)],
                  });
                }
              });
            }),
            takeUntil(this._destroy$),
          )
          .subscribe();
        break;
      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  }
}
